/**
 * Time is an argument, never an ambient global.
 *
 * The prototype pins `today()` to 13 June 2026 so seeded data renders well
 * (design/Clinic Stock v2.dc.html:3121). Production runs on real time in
 * Europe/London — and expiry windows, "this month" target math, and the monthly
 * reset are all timezone-sensitive. So: no function in this package calls
 * `new Date()`. Callers pass a `today` (a LocalDate), which makes every one of
 * these functions trivially testable and makes the timezone decision explicit
 * at exactly one place per entry point.
 */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
export function isLocalDate(s) {
    return typeof s === 'string' && ISO_DATE.test(s);
}
/** Today in a given IANA zone. The only place real time enters the domain. */
export function todayIn(timeZone = 'Europe/London', now = new Date()) {
    // en-CA gives YYYY-MM-DD, which is the format we want.
    return new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(now);
}
function toUTC(d) {
    const [y, m, day] = d.split('-').map(Number);
    return Date.UTC(y, m - 1, day);
}
export function daysBetween(from, to) {
    return Math.round((toUTC(to) - toUTC(from)) / 86_400_000);
}
/**
 * Whole weeks from `today` until `date`, rounded — the prototype's `weeksUntil`
 * (design/Clinic Stock v2.dc.html:3125). Negative once the date has passed.
 */
export function weeksUntil(date, today) {
    return Math.round(daysBetween(today, date) / 7);
}
/**
 * Calendar-date arithmetic, done in UTC on purpose.
 *
 * Expiry is a date, not an instant. Anchoring at midnight UTC keeps the
 * arithmetic free of daylight-saving edges: adding 28 days to a date in March
 * must give the same answer whether or not the clocks changed in between.
 */
export function addDays(date, days) {
    return new Date(Date.parse(`${date}T00:00:00Z`) + days * 86_400_000)
        .toISOString()
        .slice(0, 10);
}
export function addMonths(date, months) {
    const [y, m, d] = date.split('-').map(Number);
    const shifted = new Date(Date.UTC(y, m - 1 + months, d));
    return shifted.toISOString().slice(0, 10);
}
/** "13 Jun 2026" — the prototype's `fmtDate`. */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export function formatDate(date) {
    if (!date || !isLocalDate(date))
        return '—';
    const [y, m, d] = date.split('-');
    return `${Number(d)} ${MONTHS[Number(m) - 1]} ${y}`;
}
/** "2026-06" — the key for monthly_targets rows. */
export function monthKey(date) {
    return date.slice(0, 7);
}
//# sourceMappingURL=clock.js.map